import * as React from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { supabase } from '@/lib/supabase.js'
import { Button } from '@/components/ui/button.jsx'
import { Field } from '@/components/ui/field.jsx'
import { Alert, Spinner } from '@/components/ui/feedback.jsx'

export function AcceptInvitePage () {
  const navigate = useNavigate()
  const [status, setStatus] = React.useState('verifying')
  const [password, setPassword] = React.useState('')
  const [confirm, setConfirm] = React.useState('')
  const [submitting, setSubmitting] = React.useState(false)
  const [error, setError] = React.useState(null)
  const [done, setDone] = React.useState(false)

  React.useEffect(() => {
    let active = true

    async function establishInviteSession () {
      const params = new URLSearchParams(window.location.search)
      const code = params.get('code')
      if (code) {
        const { error: exErr } = await supabase.auth.exchangeCodeForSession(code)
        if (!active) return
        if (exErr) { setStatus('invalid'); return }
        window.history.replaceState({}, '', window.location.pathname)
        setStatus('ready')
        return
      }

      const tokenHash = params.get('token_hash')
      if (tokenHash) {
        const { error: otpErr } = await supabase.auth.verifyOtp({ token_hash: tokenHash, type: 'invite' })
        if (!active) return
        if (otpErr) { setStatus('invalid'); return }
        window.history.replaceState({}, '', window.location.pathname)
        setStatus('ready')
        return
      }

      const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''))
      const accessToken = hash.get('access_token')
      const refreshToken = hash.get('refresh_token')
      if (accessToken && refreshToken && hash.get('type') === 'invite') {
        const { error: setErr } = await supabase.auth.setSession({
          access_token: accessToken, refresh_token: refreshToken
        })
        if (!active) return
        if (setErr) { setStatus('invalid'); return }
        window.history.replaceState({}, '', window.location.pathname)
        setStatus('ready')
        return
      }

      if (active) setStatus('invalid')
    }

    establishInviteSession()
    return () => { active = false }
  }, [])

  async function handleSubmit (event) {
    event.preventDefault()
    setError(null)
    if (password.length < 8) {
      setError('Use at least 8 characters.')
      return
    }
    if (password !== confirm) {
      setError('Those passwords do not match.')
      return
    }
    setSubmitting(true)
    try {
      const { error: updErr } = await supabase.auth.updateUser({ password })
      if (updErr) throw updErr
      setDone(true)
    } catch (err) {
      setError(err?.message ?? 'Could not create your password. Try again.')
      setSubmitting(false)
    }
  }

  if (done) return <Navigate to="/" replace />

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center bg-canvas px-4 py-12">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <h1 className="text-2xl font-semibold tracking-tight text-ink">MBOS</h1>
          <p className="mt-1.5 text-sm text-ink-muted">Create your password</p>
        </div>

        {status === 'verifying' && (
          <div className="flex justify-center py-8"><Spinner label="Checking your invite" /></div>
        )}

        {status === 'invalid' && (
          <div className="space-y-4 rounded-lg border border-line bg-surface p-6 shadow-card">
            <Alert tone="danger" title="This invite is not valid">
              It may have expired or already been used. Ask the workspace owner to send a new invite.
            </Alert>
            <Button className="w-full" onClick={() => navigate('/login')}>
              Back to sign in
            </Button>
          </div>
        )}

        {status === 'ready' && (
          <form
            onSubmit={handleSubmit}
            className="space-y-5 rounded-lg border border-line bg-surface p-6 shadow-card"
            noValidate
          >
            {error && <Alert tone="danger">{error}</Alert>}

            <Field
              id="password"
              label="Password"
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              autoComplete="new-password"
              required
              disabled={submitting}
            />
            <Field
              id="confirm"
              label="Confirm password"
              type="password"
              value={confirm}
              onChange={e => setConfirm(e.target.value)}
              autoComplete="new-password"
              required
              disabled={submitting}
            />

            <Button type="submit" className="w-full" loading={submitting}>
              {submitting ? 'Saving' : 'Join workspace'}
            </Button>
          </form>
        )}
      </div>
    </div>
  )
}
